import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Modal, { ModalRef } from '@/components/@IntranetLibrary/Modal';
import { Body1 } from '@/components/@IntranetLibrary/Typography';
import PassportItem from '@/components/Record/SubjectContent/PassportItem';
import type { InternalParticipant } from '@/types/api';

/**
 * 미권한자 "참조인 추가 후 등록" 확인 모달 — open 이 Promise<boolean> 게이트.
 * 실제 참조인·코멘트 등록은 호출처(CommentSlot)가 수행.
 */
export type MentionRefereeModalRef = {
  /** 미권한자 리스트로 모달을 열고, true(참조인 추가 후 등록) / false(취소·닫기) 를 resolve. */
  open: (people: InternalParticipant[]) => Promise<boolean>;
  close: () => void;
};

const MentionRefereeModal = forwardRef<MentionRefereeModalRef>(
  (_props, ref) => {
    const { t } = useTranslation();
    const modalRef = useRef<ModalRef>(null);
    const [people, setPeople] = useState<InternalParticipant[]>([]);

    const resolverRef = useRef<((v: boolean) => void) | null>(null);
    const settledRef = useRef(false);

    const settle = (v: boolean) => {
      if (settledRef.current) return;
      settledRef.current = true;
      resolverRef.current?.(v);
      resolverRef.current = null;
      modalRef.current?.close();
    };

    useImperativeHandle(ref, () => ({
      open: (list) =>
        new Promise<boolean>((resolve) => {
          settledRef.current = false;
          resolverRef.current = resolve;
          setPeople(list);
          modalRef.current?.open();
        }),
      close: () => settle(false),
    }));

    return (
      <Modal
        ref={modalRef}
        title=""
        size="small"
        handleClose={() => settle(false)}
        sx={{
          '.modal-body': { overflow: 'visible', paddingBottom: 0 },
          '.small': { minWidth: '480px !important' },
          '.modal-footer': {
            backgroundColor: 'var(--dk-bg, white)',
            paddingTop: 0,
            button: { minWidth: '120px' },
          },
        }}
        footerContent={
          <Stack direction="row" justifyContent="flex-end" gap="8px" mt="24px">
            <Button
              variant="outlined"
              size="large"
              onClick={() => settle(false)}
            >
              {t('취소')}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => settle(true)}
            >
              {t('참조인 추가 후 등록')}
            </Button>
          </Stack>
        }
      >
        <Stack gap="24px">
          <Body1 sx={{ whiteSpace: 'pre-line', fontWeight: 400 }}>
            {t(
              '멘션한 사람 중 해당 문서의 조회 권한이 없는 사람이 있습니다.\n코멘트 등록 시 아래 인원이 참조인으로 추가됩니다.',
            )}
          </Body1>
          <Stack
            sx={{
              padding: '12px',
              gap: '8px',
              borderRadius: '8px',
              background: 'var(--dk-bg-subtle, #f9fafb)',
            }}
          >
            {people.map((p) => (
              <PassportItem key={p.passportId} passport={p} />
            ))}
          </Stack>
        </Stack>
      </Modal>
    );
  },
);

MentionRefereeModal.displayName = 'MentionRefereeModal';

export default MentionRefereeModal;
